import React, { useState } from "react";
import { useAuth } from "../AuthContext";

// Klein invite-paneeltje voor in de videoplayer-overlay
export default function BondMiniInvitePanel({
  friends,
  busy = false,
  onInvite,   // async (friendIds: string[])
  onClose,    // () => void
}) {
  const { currentUser, login } = useAuth();
  const isLoggedIn = !!currentUser;

  const [selectedIds, setSelectedIds] = useState(new Set());

  // Voor mini-login als je nog niet ingelogd bent
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [error, setError] = useState("");

  const hasFriends = friends && friends.length > 0;
  const hasSelection = selectedIds.size > 0;
  const isBusy = busy || authBusy;

  function toggle(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleInviteClick() {
    if (!hasSelection || !onInvite || isBusy) return;
    const ids = Array.from(selectedIds);
    await onInvite(ids);
    setSelectedIds(new Set());
    onClose?.();
  }

  async function handleLoginClick() {
    if (isBusy) return;
    setError("");

    try {
      setAuthBusy(true);
      await login(email, password);
      // Na succes: AuthContext zet currentUser/token;
      // App laadt friends → panel rerendert automatisch met vriendenlijst.
      setEmail("");
      setPassword("");
    } catch (err) {
      const code = err?.code || err?.message || "ERROR";
      if (code === "INVALID_CREDENTIALS") {
        setError("Onjuiste inloggegevens.");
      } else if (code === "EMAIL_AND_PASSWORD_REQUIRED") {
        setError("Vul een e-mailadres en wachtwoord in.");
      } else {
        setError("Inloggen lukt nu even niet.");
      }
    } finally {
      setAuthBusy(false);
    }
  }

  // Tekstje onderin
  let helperText = "";
  if (!isLoggedIn) {
    helperText = "Log in om je Bond-vrienden te zien.";
  } else if (!hasFriends) {
    helperText = "Je hebt nog geen Bond-vrienden om uit te nodigen.";
  } else if (!hasSelection) {
    helperText = "Kies één of meer vrienden om uit te nodigen.";
  }

  // Primary button state + label
  const primaryDisabled = isLoggedIn
    ? !hasSelection || isBusy
    : !email.trim() || !password.trim() || isBusy;

  const primaryLabel = isBusy
    ? "Bezig..."
    : isLoggedIn
    ? "Invite sturen"
    : "Inloggen";

  const primaryOnClick = isLoggedIn ? handleInviteClick : handleLoginClick;

 return (
  <div
    style={{
      position: "absolute",
      bottom: 44,
      right: 0,
      width: 240,
      background: "#020617",
      borderRadius: 14,
      boxShadow: "0 20px 50px rgba(15,23,42,0.9)",
      border: "1px solid rgba(148,163,184,0.35)",
      overflow: "hidden",
      zIndex: 40,
    }}
    onClick={(e) => e.stopPropagation()} // niet bubbelen naar video
    >
      {/* Header */}
      <div
        style={{
          padding: "8px 10px 6px 10px",
          borderBottom: "1px solid rgba(31,41,55,0.9)",
        }}
      >
        <div
          style={{
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: "#9ca3af",
          }}
        >
          Bond-vrienden
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
          {isLoggedIn ? "Nodig snel iemand uit" : "Log in en nodig vrienden uit"}
        </div>
      </div>

      {/* Scrollbare inhoud */}
      <div
        className="bond-scroll"
        style={{
          maxHeight: 190,
          overflowY: "auto",
          padding: "6px 10px 4px 10px",
        }}
      >
        {isLoggedIn ? (
          // ---- Vriendenlijst (zoals je basis) ----
          hasFriends ? (
            friends.map((f) => {
              const id = String(f.id);
              const selected = selectedIds.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => toggle(id)}
                  style={{
                    width: "100%",
                    padding: "6px 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: selected
                      ? "rgba(79,70,229,0.18)"
                      : "transparent",
                    border: "none",
                    color: "#e5e7eb",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                      marginRight: 8,
                    }}
                  >
                    {f.name || f.email || `User ${f.id}`}
                  </span>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      border: selected
                        ? "0"
                        : "1px solid rgba(148,163,184,0.7)",
                      background: selected
                        ? "linear-gradient(135deg,#4f46e5,#ec4899)"
                        : "transparent",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                    }}
                  >
                    {selected ? "✓" : ""}
                  </span>
                </button>
              );
            })
          ) : (
            <div
              style={{
                padding: "4px 0 8px 0",
                fontSize: 12,
                color: "#9ca3af",
              }}
            >
              Je hebt nog geen Bond-vrienden om uit te nodigen.
            </div>
          )
        ) : (
          // ---- Mini-login als je NIET ingelogd bent ----
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ textAlign: "left" }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  marginBottom: 2,
                  color: "#e5e7eb",
                }}
              >
                E-mailadres
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 999,
                  border: "1px solid rgba(75,85,99,0.9)",
                  background: "#020617",
                  color: "#e5e7eb",
                  fontSize: 12,
                }}
                placeholder="jij@example.com"
              />
            </div>

            <div style={{ textAlign: "left" }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  marginBottom: 2,
                  color: "#e5e7eb",
                }}
              >
                Wachtwoord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 999,
                  border: "1px solid rgba(75,85,99,0.9)",
                  background: "#020617",
                  color: "#e5e7eb",
                  fontSize: 12,
                }}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                style={{
                  fontSize: 11,
                  color: "#fca5a5",
                  marginTop: 2,
                  textAlign: "left",
                }}
              >
                {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky onderkant: status + primaire knop */}
      <div
        style={{
          padding: "6px 10px 9px 10px",
          borderTop: "1px solid rgba(31,41,55,0.9)",
          background:
            "radial-gradient(circle at top, rgba(79,70,229,0.2), transparent 55%)",
        }}
      >
        {helperText && (
          <div
            style={{
              fontSize: 11,
              color: "#9ca3af",
              marginBottom: 4,
            }}
          >
            {helperText}
          </div>
        )}

        <button
          type="button"
          disabled={primaryDisabled}
          onClick={primaryOnClick}
          style={{
            width: "100%",
            padding: "6px 12px",
            borderRadius: 999,
            border: "none",
            fontSize: 12,
            fontWeight: 500,
            cursor: primaryDisabled ? "default" : "pointer",
            opacity: primaryDisabled ? 0.6 : 1,
            background:
              "linear-gradient(135deg, #4f46e5 0%, #6366f1 40%, #ec4899 100%)",
            color: "#fff",
            boxShadow: "0 10px 30px rgba(15,23,42,0.9)",
          }}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}
